const ELEMENT_NODE = 1
const TEXT_NODE = 3
const LIST_TAGS = new Set(["OL", "UL"])

export function resolveRichTextPreviewMode({ originalValue, tailoredValue }) {
    if (hasNestedListStructure(originalValue) || hasNestedListStructure(tailoredValue)) {
        return "structured"
    }

    return "diff"
}

export function buildStructuredRichTextPreviewHtml({ originalValue, tailoredValue, side = "new" }) {
    const originalRoot = createContainer(originalValue)
    const tailoredRoot = createContainer(tailoredValue)
    const originalBlocks = collectStructuredBlocks(originalRoot)
    const tailoredBlocks = collectStructuredBlocks(tailoredRoot)
    const operations = buildSegmentDiff(
        originalBlocks.map((block) => normalizeSegment(block.text)),
        tailoredBlocks.map((block) => normalizeSegment(block.text))
    )
    const changedOriginalIndexes = new Set()
    const changedTailoredIndexes = new Set()

    for (const operation of operations) {
        if (operation.type === "delete") {
            changedOriginalIndexes.add(operation.leftIndex)
            continue
        }

        if (operation.type === "insert") {
            changedTailoredIndexes.add(operation.rightIndex)
        }
    }

    const targetRoot = side === "original" ? originalRoot : tailoredRoot
    const targetBlocks = side === "original" ? originalBlocks : tailoredBlocks
    const changedIndexes = side === "original" ? changedOriginalIndexes : changedTailoredIndexes
    const marker = side === "original" ? "removed" : "new"

    for (const index of changedIndexes) {
        wrapStructuredBlock(targetBlocks[index], marker)
    }

    return targetRoot.innerHTML
}

function hasNestedListStructure(value = "") {
    if (!value) return false

    const container = createContainer(value)
    return Boolean(container.querySelector("ol ol, ol ul, ul ol, ul ul"))
}

function createContainer(value = "") {
    const container = document.createElement("div")
    container.innerHTML = `${value || ""}`
    return container
}

function collectStructuredBlocks(root) {
    const blocks = []

    for (const child of root.childNodes) {
        collectNodeBlocks(child, blocks)
    }

    return blocks
}

function collectNodeBlocks(node, blocks) {
    if (!node) return

    if (node.nodeType === TEXT_NODE) {
        const text = normalizeWhitespace(node.nodeValue)
        if (text) {
            blocks.push({ type: "text", node, text })
        }
        return
    }

    if (node.nodeType !== ELEMENT_NODE) return

    switch (node.tagName) {
        case "OL":
        case "UL":
            collectListBlocks(node, blocks)
            return
        case "P":
            addElementTextBlock(node, blocks)
            return
        case "DIV":
            collectDivBlocks(node, blocks)
            return
        default:
            for (const child of node.childNodes) {
                collectNodeBlocks(child, blocks)
            }
    }
}

function collectListBlocks(list, blocks) {
    for (const child of list.children) {
        if (child.tagName !== "LI") continue
        addListItemBlock(child, blocks)
    }
}

function addListItemBlock(listItem, blocks) {
    const text = getListItemDirectText(listItem)
    if (text) {
        blocks.push({ type: "li", node: listItem, text })
    }

    for (const child of listItem.children) {
        if (LIST_TAGS.has(child.tagName)) {
            collectListBlocks(child, blocks)
        }
    }
}

function collectDivBlocks(node, blocks) {
    if (!hasNestedBlockElements(node)) {
        addElementTextBlock(node, blocks)
        return
    }

    const directText = getDirectTextExcludingLists(node)
    if (directText) {
        blocks.push({ type: "div", node, text: directText })
    }

    for (const child of node.childNodes) {
        collectNodeBlocks(child, blocks)
    }
}

function hasNestedBlockElements(node) {
    for (const child of node.children) {
        if (["DIV", "P", "OL", "UL", "LI"].includes(child.tagName)) return true
    }

    return false
}

function addElementTextBlock(node, blocks) {
    const text = normalizeWhitespace(node.textContent)
    if (!text) return
    blocks.push({ type: node.tagName.toLowerCase(), node, text })
}

function getListItemDirectText(node) {
    const parts = []

    for (const child of node.childNodes) {
        if (child.nodeType === TEXT_NODE) {
            parts.push(child.nodeValue)
            continue
        }

        if (child.nodeType !== ELEMENT_NODE || LIST_TAGS.has(child.tagName)) continue
        parts.push(child.textContent)
    }

    return normalizeWhitespace(parts.join(" "))
}

function getDirectTextExcludingLists(node) {
    const parts = []

    for (const child of node.childNodes) {
        if (child.nodeType === TEXT_NODE) {
            parts.push(child.nodeValue)
            continue
        }

        if (child.nodeType !== ELEMENT_NODE || LIST_TAGS.has(child.tagName)) continue
        if (["DIV", "P", "LI"].includes(child.tagName)) continue
        parts.push(child.textContent)
    }

    return normalizeWhitespace(parts.join(" "))
}

function buildSegmentDiff(originalSegments, nextSegments) {
    const dp = buildLcsTable(originalSegments, nextSegments)
    const operations = []

    let leftIndex = 0
    let rightIndex = 0

    while (leftIndex < originalSegments.length && rightIndex < nextSegments.length) {
        if (originalSegments[leftIndex] === nextSegments[rightIndex]) {
            operations.push({ type: "equal", leftIndex, rightIndex })
            leftIndex += 1
            rightIndex += 1
            continue
        }

        if (dp[leftIndex + 1][rightIndex] >= dp[leftIndex][rightIndex + 1]) {
            operations.push({ type: "delete", leftIndex })
            leftIndex += 1
            continue
        }

        operations.push({ type: "insert", rightIndex })
        rightIndex += 1
    }

    while (leftIndex < originalSegments.length) {
        operations.push({ type: "delete", leftIndex })
        leftIndex += 1
    }

    while (rightIndex < nextSegments.length) {
        operations.push({ type: "insert", rightIndex })
        rightIndex += 1
    }

    return operations
}

function buildLcsTable(left, right) {
    const table = Array.from({ length: left.length + 1 }, () => Array(right.length + 1).fill(0))

    for (let leftIndex = left.length - 1; leftIndex >= 0; leftIndex -= 1) {
        for (let rightIndex = right.length - 1; rightIndex >= 0; rightIndex -= 1) {
            if (left[leftIndex] === right[rightIndex]) {
                table[leftIndex][rightIndex] = table[leftIndex + 1][rightIndex + 1] + 1
                continue
            }

            table[leftIndex][rightIndex] = Math.max(table[leftIndex + 1][rightIndex], table[leftIndex][rightIndex + 1])
        }
    }

    return table
}

function wrapStructuredBlock(block, marker) {
    if (!block?.node) return

    if (block.type === "li") {
        wrapListItemDirectContent(block.node, marker)
        return
    }

    if (block.type === "text") {
        wrapTextNode(block.node, marker)
        return
    }

    wrapNodeContents(block.node, marker)
}

function wrapListItemDirectContent(node, marker) {
    const span = createStructuredDiffMarker(node.ownerDocument, marker)
    let hasContent = false

    for (const child of [...node.childNodes]) {
        if (child.nodeType === TEXT_NODE) {
            if (!normalizeWhitespace(child.nodeValue)) continue
            span.appendChild(child)
            hasContent = true
            continue
        }

        if (child.nodeType !== ELEMENT_NODE || LIST_TAGS.has(child.tagName)) continue
        span.appendChild(child)
        hasContent = true
    }

    if (!hasContent) return
    node.insertBefore(span, node.firstChild)
}

function wrapTextNode(node, marker) {
    const text = normalizeWhitespace(node.nodeValue)
    if (!text) return

    const span = createStructuredDiffMarker(node.ownerDocument, marker)
    span.textContent = node.nodeValue
    node.parentNode.replaceChild(span, node)
}

function wrapNodeContents(node, marker) {
    if (!node?.childNodes?.length) return

    const span = createStructuredDiffMarker(node.ownerDocument, marker)
    for (const child of [...node.childNodes]) {
        span.appendChild(child)
    }
    node.appendChild(span)
}

function createStructuredDiffMarker(documentRef, marker) {
    const span = documentRef.createElement("span")
    span.setAttribute("data-structured-diff", marker)
    return span
}

function normalizeSegment(value) {
    return `${value || ""}`.replace(/\s+/g, " ").trim().toLowerCase()
}

function normalizeWhitespace(value) {
    return `${value || ""}`.replace(/\s+/g, " ").trim()
}
